import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IDeleteParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IMonopolyTurnGame } from './monopoly-turn-game.interface';
import { Observable, map, tap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl3-monopolyTurn-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IMonopolyTurnGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class MonopolyTurnGameCoreService {
  private _loading = false;

  /**
   * @name monopolyTurnGameList$
   * @description emits stored list of the companies
   */

  monopolyTurnGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getMonopolyTurnGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`monopoly-turn-game`, params || {})
      .subscribe((data: IMonopolyTurnGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addMonopolyTurnGame(dataInput): Observable<IMonopolyTurnGame> {
    this._loading = true;
    return this._api.post('monopoly-turn-game', dataInput || {}).pipe(
      tap((data: IMonopolyTurnGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateMonopolyTurnGame(dataInput): Observable<IMonopolyTurnGame> {
    this._loading = true;
    return this._api.put('monopoly-turn-game', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IMonopolyTurnGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteMonopolyTurnGame(dataInput: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('monopoly-turn-game', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public getMonopolyTurnGameById(id: string): Observable<IMonopolyTurnGame> {
    return this._api.get(`monopoly-turn-game/${id}`);
  }

  public getMonopolyTurnGameInfo(id: string): IMonopolyTurnGame {
    const monopolyTurnGame = store.query(getEntity(id));
    return monopolyTurnGame;
  }

  public resolveMonopolyTurnGameId(
    monopolyTurnGame: string | IMonopolyTurnGame,
  ): string {
    let monopolyTurnGameId: string | IMonopolyTurnGame = '';
    if (monopolyTurnGame) {
      monopolyTurnGameId = monopolyTurnGame;
    }
    if (monopolyTurnGame['_id']) {
      monopolyTurnGameId = monopolyTurnGame['_id'];
    }
    return monopolyTurnGameId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getMonopolyTurnGames();
    }
  }
}

interface IMonopolyTurnGameListResponse {
  list: IMonopolyTurnGame[];
  totalCount: number;
}
